import { useState, type ReactNode, useEffect } from 'react';
import { Link, Navigate, useRouterState } from '@tanstack/react-router';
import { useSession, useOwnOnly } from '../../lib/auth/session.js';
import { PhoneProvider } from '../../features/agent/PhoneProvider.js';
import { CallBar } from '../../features/agent/CallBar.js';
import { logout } from '../../lib/api/client.js';
import { Icon, BrandMark, type IconName } from '../ui/Icon.js';
import { UpdateBanner } from './UpdateBanner.js';
import { SuspendedNotice } from './SuspendedNotice.js';
import { ThemeToggle } from './ThemeToggle.js';
import { UserMenu } from './UserMenu.js';
import { PageTabs } from './PageTabs.js';
import { usePageTabs } from '../../lib/pagetabs.js';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** Held, or any one of several (the broad permission or its own form). */
  permission?: string | readonly string[];
  /** Not for somebody who sees only their own seats (useOwnOnly). */
  notOwnOnly?: boolean;
  /**
   * MSP console only. REQUIRED on every platform destination -- a permission
   * alone is not a gate here.
   *
   * BUILTIN_ROLES derives its read-only role as
   * PERMISSION_KEYS.filter(k => k.endsWith(':read')) (contracts/permissions.ts),
   * which hands a TENANT read-only user audit:read, user:read, number:read,
   * billing:read, invoice:read and ratedeck:read. So gating an MSP item on a
   * ':read' permission gates nothing: every one of these was visible to
   * customers, and /platform/settings had no gate at all.
   *
   * The APIs behind them assert platform scope, so no customer data leaked --
   * but showing a customer the operator console is its own problem, and it is
   * the exact split the platform is meant to enforce.
   */
  platformOnly?: boolean;
  /** Client portal only -- realtime supervision, the customer's own PBX. */
  tenantOnly?: boolean;
  /** Only for the provider -- the platform or a reseller -- never for an account's own people. */
  providerOnly?: boolean;
  /** Not for an account's own people: their home is their account's TMS instead. */
  notTenant?: boolean;
  /**
   * Only where one of this person's own seats takes calls in the browser (bootstrap `workspace`,
   * 0105). The workspace is an agent's screen: offering it to a seat that cannot use it is a
   * link to a dead end, and the API refuses the phone anyway.
   */
  workspaceOnly?: boolean;
}
interface NavSection { title: string; items: NavItem[] }

/**
 * Operator console navigation.
 *
 * Accounts, products, inventory, billing. Per-tenant telephony configuration
 * lives behind an account, not up here (doc 24) — two places to change one
 * setting is how they diverge.
 */
/**
 * Pages a client's admins and managers now have in their account console (owner, 3 October 2026:
 * "Console only"), and where. For them the outer menu leaves these out and the old addresses go
 * to the console page. People who see only their own seat ("own only") keep these pages: the
 * console's sections need account-wide permissions they do not have. /my/billing is left where
 * it is: PayFast sends payers back to /my/billing/payments.
 */
const IN_THE_CONSOLE: ReadonlyArray<readonly [string, string]> = [
  ['/realtime/extensions', 'live-extensions'], ['/realtime/queues', 'live-queues'],
  ['/realtime/wallboard', 'wallboard'], ['/realtime/attendance', 'agent-activity'],
  ['/my/extensions', 'extensions'], ['/my/calls', 'calls'],
];

const NAV: NavSection[] = [
  {
    title: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', icon: 'dashboard', notOwnOnly: true, notTenant: true },
      // An account's own admin: their account's TMS is their home (26 September 2026).
      { to: '/', label: 'Your TMS', icon: 'settings', notOwnOnly: true, tenantOnly: true },
      { to: '/activity', label: 'Activity log', icon: 'clock', permission: 'audit:read', platformOnly: true },
    ],
  },
  {
    /**
     * What is happening RIGHT NOW, as opposed to what happened.
     *
     * Its own section rather than one "Live" item under Overview, because there
     * is more than one live view and they are read in different situations:
     * "is anyone's phone broken" and "is anyone waiting on hold" are different
     * questions asked by different people.
     *
     * Paths do not share a prefix. The sidebar marks an item active with
     * startsWith, so /realtime and /realtime/queues would both light up.
     *
     * TENANT-ONLY, which is the deliberate half of this. Watching phones and
     * queues in real time is a CUSTOMER's tool -- a supervisor deciding whether
     * to pull someone onto the floor. Platform staff open a customer from the
     * account list to answer a commercial or provisioning question, and putting
     * a live wallboard in that path answers a question nobody asked while
     * implying the console is where you go to supervise somebody else's team.
     */
    title: 'Realtime',
    items: [
      { to: '/realtime/extensions', label: 'Extensions', icon: 'extension', permission: 'extension:read', tenantOnly: true },
      { to: '/realtime/queues', label: 'Queues', icon: 'queue', permission: 'queue:read', tenantOnly: true },
      // The contact centre on the office TV (26 September 2026).
      { to: '/realtime/wallboard', label: 'Wallboard', icon: 'dashboard', permission: 'queue:read', tenantOnly: true },
      // Who was logged on, what their time went on, and what their calls were: the workspace's
      // shifts and dispositions, added up.
      { to: '/realtime/attendance', label: 'Agent activity', icon: 'clock', permission: 'cdr:read', tenantOnly: true },
    ],
  },
  {
    // A tenant-scoped user has one account and no list to drill through, so
    // their own PBX is surfaced directly.
    title: 'My PBX',
    items: [
      /**
       * The workspace opens in the SAME window, and takes it over: a shift is not a page of the
       * console (docs/36 SS8). Offered only where one of this person's own seats can take calls
       * in the browser -- `workspaceOnly` below -- because it is an agent's screen and a seat
       * without the feature reaches a dead end there (owner, 24 September 2026).
       */
      { to: '/agent', label: 'Workspace', icon: 'headset', permission: ['extension:read', 'extension:read:own'], tenantOnly: true, workspaceOnly: true },
      { to: '/my/extensions', label: 'Extensions', icon: 'extension', permission: ['extension:read', 'extension:read:own'], tenantOnly: true },
      { to: '/my/calls', label: 'Call history', icon: 'phone', permission: ['cdr:read', 'cdr:read:own'], tenantOnly: true },
      { to: '/my/billing', label: 'Billing', icon: 'billing', permission: 'billing:read', tenantOnly: true },
    ],
  },
  {
    title: 'Accounts',
    items: [
      { to: '/accounts', label: 'Customer accounts', icon: 'accounts', permission: 'tenant:read', notOwnOnly: true, providerOnly: true },
      { to: '/fraud', label: 'Fraud', icon: 'warning', permission: 'fraud:read', notOwnOnly: true, providerOnly: true },
      { to: '/rica', label: 'RICA reviews', icon: 'shield', permission: 'rica:review', notOwnOnly: true, providerOnly: true },
      { to: '/resellers', label: 'Resellers', icon: 'resellers', permission: 'reseller:read', platformOnly: true },
      { to: '/users', label: 'Portal users', icon: 'users', permission: 'user:read', platformOnly: true },
    ],
  },
  {
    title: 'Products',
    items: [
      { to: '/products/extension-types', label: 'Extension types', icon: 'extension', permission: 'tenant:read', platformOnly: true },
      { to: '/products/call-plans', label: 'Call plans', icon: 'plan', permission: 'ratedeck:read', platformOnly: true },
      { to: '/products/rate-sheets', label: 'Rate sheets', icon: 'rates', permission: 'ratedeck:read', platformOnly: true },
    ],
  },
  {
    title: 'Inventory',
    items: [
      { to: '/inventory/carriers', label: 'Carriers & trunks', icon: 'carrier', permission: 'carrier:read', platformOnly: true },
      { to: '/inventory/numbers', label: 'DID numbers', icon: 'hash', permission: 'number:read', platformOnly: true },
      { to: '/inventory/handsets', label: 'Handsets', icon: 'phone', permission: 'device:read', platformOnly: true },
      { to: '/inventory/templates', label: 'Phone templates', icon: 'plan', permission: 'device:read', platformOnly: true },
    ],
  },
  {
    title: 'Billing',
    items: [
      { to: '/billing/runs', label: 'Billing runs', icon: 'billing', permission: 'billing:read', platformOnly: true },
      { to: '/billing/invoices', label: 'Invoices', icon: 'invoice', permission: 'invoice:read', platformOnly: true },
      { to: '/billing/setup', label: 'Billing setup', icon: 'billing', permission: 'billing:write', platformOnly: true },
    ],
  },
  {
    title: 'Platform',
    items: [
      { to: '/platform/nodes', label: 'Nodes', icon: 'server', permission: 'node:read', platformOnly: true },
      { to: '/platform/jobs', label: 'Scheduled jobs', icon: 'clock', permission: 'node:read', platformOnly: true },
      { to: '/platform/provider-keys', label: 'Provider keys', icon: 'settings', permission: 'providerkey:read', platformOnly: true },
      { to: '/platform/recording-storage', label: 'Recording storage', icon: 'media', permission: 'node:read', platformOnly: true },
      { to: '/platform/sip-security', label: 'SIP security', icon: 'globe', permission: 'node:read', platformOnly: true },
      { to: '/platform/log-watch', label: 'Log watch', icon: 'shield', permission: 'node:read', platformOnly: true },
      { to: '/platform/settings', label: 'Settings', icon: 'settings', platformOnly: true },
    ],
  },
];

/** Pages reached other than from the menu, which open as tabs too. */
const OTHER_PAGES: ReadonlyArray<{ to: string; label: string }> = [
  { to: '/account/security', label: 'Your account' },
];

/** The menu entry a page belongs to: the longest one its address is under. */
function pageOf(pathname: string): string | null {
  let best: string | null = null;
  for (const { to } of [...NAV.flatMap((s) => s.items), ...OTHER_PAGES]) {
    const hit = to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
    if (hit && (best === null || to.length > best.length)) best = to;
  }
  return best;
}

export function AppShell({ children }: { children: ReactNode }) {
  const session = useSession((s) => s.session);
  const can = useSession((s) => s.can);
  const clear = useSession((s) => s.clear);
  const [chosen, setCollapsed] = useState(
    () => { try { return localStorage.getItem('arc.nav.collapsed') === '1'; } catch { return false; } },
  );
  /**
   * Narrow screens get the icon rail whatever was chosen (3 October 2026): the stylesheet narrows the
   * sidebar under 900px, and the labels, still drawn, ran under the page. Every item stays on screen
   * as its icon, with its name on hover.
   */
  const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width: 900px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const on = (): void => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const collapsed = chosen || narrow;
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const isPlatform = session?.scope.level === 'platform';
  const ownOnly = useOwnOnly();
  const isTenantScoped = session?.scope.level === 'tenant' || session?.scope.level === 'site';
  const hasWorkspace = session?.workspace ?? false;
  const accountName = isTenantScoped
    ? (session?.memberships.find((m) => m.tenant_id === session.active_tenant_id) ?? session?.memberships[0])?.tenant_name ?? null
    : null;

  const toggle = () => {
    setCollapsed((v) => {
      try { localStorage.setItem('arc.nav.collapsed', v ? '0' : '1'); } catch { /* ignore */ }
      return !v;
    });
  };

  // A client's admin or manager: their account console is home (IN_THE_CONSOLE).
  const consoleUser = isTenantScoped && !ownOnly;
  const visible = (i: NavItem): boolean => {
    if (consoleUser && (IN_THE_CONSOLE.some(([p]) => p === i.to) || i.to === '/my/billing')) return false;
    if (i.permission && !(typeof i.permission === 'string' ? [i.permission] : i.permission).some(can)) return false;
    if (i.notOwnOnly && ownOnly) return false;
    if (i.platformOnly && !isPlatform) return false;
    if (i.tenantOnly && !isTenantScoped) return false;
    if ((i.providerOnly || i.notTenant) && isTenantScoped) return false;
    if (i.workspaceOnly && !hasWorkspace) return false;
    return true;
  };

  /**
   * A reseller's brand (migration 0087): its colour replaces ArcNet's green across the portal for
   * its people and its clients, and its name titles the tab. Set on the root, where the tokens
   * live, and taken off again for anyone without one.
   */
  const brand = session?.brand;
  const level = session?.scope.level;
  useEffect(() => {
    const root = document.documentElement.style;
    const names = ['--arc-brand', '--arc-accent', '--arc-accent-hover', '--arc-brand-from', '--arc-brand-to',
      '--arc-nav-accent'];
    if (brand?.custom) {
      root.setProperty('--arc-brand', brand.accent);
      root.setProperty('--arc-accent', brand.accent);
      root.setProperty('--arc-accent-hover', brand.accent);
      root.setProperty('--arc-brand-from', brand.gradient_from ?? brand.accent);
      root.setProperty('--arc-brand-to', brand.gradient_to ?? brand.accent);
      root.setProperty('--arc-nav-accent', brand.gradient_to ?? brand.accent);
    } else {
      for (const n of names) root.removeProperty(n);
    }
    // The console for staff; the TMS for a client's own people (owner, 18 September 2026).
    document.title = `${brand?.name ?? 'ArcNet'} - ${level === 'tenant' ? 'TMS' : 'Management Console'}`;
  }, [brand?.custom, brand?.accent, brand?.gradient_from, brand?.gradient_to, brand?.name, level]);

  /**
   * Each page opened is a tab along the top bar (lib/pagetabs.ts), named as the menu names it.
   * The menu opens a page at its start; its tab, where you were.
   */
  const pageTabs = usePageTabs({ storageKey: 'arc.tabs.console', current: pageOf(pathname), path: pathname, home: '/' });
  const tabLabel = (key: string): string => {
    const items = NAV.flatMap((s) => s.items).filter((i) => i.to === key);
    return (items.find(visible) ?? items[0])?.label ?? OTHER_PAGES.find((p) => p.to === key)?.label ?? key;
  };

  // An old address for a page that now lives in the console: there, with the rest of the path.
  const moved = consoleUser ? IN_THE_CONSOLE.find(([p]) => pathname === p || pathname.startsWith(`${p}/`)) : undefined;
  if (moved && session?.active_tenant_id) {
    return <Navigate to="/manage/$tenantId/$" replace
      params={{ tenantId: session.active_tenant_id, _splat: moved[1] + pathname.slice(moved[0].length) }} />;
  }

  return (
    // The agent's phone and shift live here, for every page (features/agent/PhoneProvider.tsx).
    <PhoneProvider>
    <div className="arc-app">
      <aside className="arc-sidebar" data-collapsed={collapsed}>
        <div className="arc-brand">
          <BrandMark size={26} brand={session?.brand} />
          {!collapsed && (
            <div className="arc-stack" style={{ minWidth: 0 }}>
              <span>{session?.brand?.name ?? 'ArcNet'}</span>
              {/* Whose console it is: an account's own people see their account here, where a
                  pill in the top bar said it before (owner, 6 October 2026). */}
              <span className="arc-brand-sub arc-truncate" style={{ maxWidth: 170 }}
                title={accountName ?? undefined}>{accountName ?? 'Console'}</span>
            </div>
          )}
        </div>

        <nav className="arc-nav" aria-label="Main">
          {NAV.map((section) => {
            const items = section.items.filter(visible);
            if (items.length === 0) return null;
            return (
              <div key={section.title}>
                {!collapsed && <div className="arc-nav-section">{section.title}</div>}
                {items.map((item) => {
                  const active = item.to === '/'
                    ? pathname === '/'
                    : pathname.startsWith(item.to);
                  return (
                    <Link
                      key={item.to} to={item.to} className="arc-nav-item"
                      data-active={active} title={collapsed ? item.label : undefined}
                    >
                      <Icon name={item.icon} size={18} />
                      {!collapsed && <span>{item.label}</span>}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="arc-sidebar-foot">
          {!narrow && <button
            type="button" className="arc-nav-item" onClick={toggle}
            style={{ width: '100%', border: 'none', background: 'none', font: 'inherit' }}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            <Icon name="panelLeft" size={16} />
            {!collapsed && <span>Collapse</span>}
          </button>}
        </div>
      </aside>

      <div className="arc-main">
        <header className="arc-header">
          <PageTabs tabs={pageTabs} labelFor={tabLabel} />
          <CallBar />

          {session?.impersonated_by && (
            <span className="arc-badge arc-badge-warn" role="status">
              <Icon name="warning" size={11} />
              Impersonating — actions are audited
            </span>
          )}

          <ThemeToggle />

          <UserMenu />
        </header>

        <main className="arc-content">
          <div className="arc-page-max"><SuspendedNotice />{children}</div>
        </main>

        <UpdateBanner />
      </div>
    </div>
    </PhoneProvider>
  );
}
